/**
 * AI CONTEXT
 * Purpose: RBAC behavior demo/sandbox for permissions and protected components.
 * Key deps: auth enums, hasPermission, ProtectedComponent variants.
 * Referenced by: App route /auth-test.
 * Importance: quick verification and documentation of permission model.
 * Refactor: move RESOURCE_ACTIONS to shared source + add test coverage.
 */
import { InfoCircleOutlined } from "@ant-design/icons"
import { Button, Popover, Select, Space, Typography } from "antd"
import { useState } from "react"

import Card from "#blocks/Card"
import Flex from "#blocks/Flex"
import {
  ContactInfoProtected,
  LoginProtected,
  ProtectedComponent,
} from "#components/ProtectedComponent"
import {
  type Resource,
  RESOURCES,
  USER_ACTIONS,
  USER_ROLES,
  type UserAction,
} from "#customTypes/composedTypes/authTypes"
import { EnumValues } from "#customTypes/types"
import { hasPermission } from "#utils/permissionsHelpers"
import { camelToWords } from "#utils/textFormatters"

const { Title, Text } = Typography

const Example = ({ title, isTrue }: { title: string; isTrue: boolean }) => {
  return (
    <li>
      {isTrue ? "✅ " : "❌ "}
      {title}
    </li>
  )
}

// Define which actions are relevant for each resource based on the permissions matrix
// Keep this in sync with https://docs.google.com/spreadsheets/d/1OIDzXccdfQo4u4Ek2oF7pZrfKUnn4d5ebt8TUTZnLZU
const RESOURCE_ACTIONS = {
  [RESOURCES.STUDENT]: [
    USER_ACTIONS.CREATE,
    USER_ACTIONS.READ,
    USER_ACTIONS.UPDATE,
    USER_ACTIONS.VIEW_CONTACT_INFO,
  ],
  [RESOURCES.CLASS]: [
    USER_ACTIONS.CREATE,
    USER_ACTIONS.READ,
    USER_ACTIONS.UPDATE,
    USER_ACTIONS.DELETE,
  ],
  [RESOURCES.CURRICULUM_PLAN]: [
    USER_ACTIONS.CREATE,
    USER_ACTIONS.READ,
    USER_ACTIONS.UPDATE,
    USER_ACTIONS.DELETE,
  ],
  [RESOURCES.PROJECT]: [
    USER_ACTIONS.CREATE,
    USER_ACTIONS.READ,
    USER_ACTIONS.UPDATE,
    USER_ACTIONS.DELETE,
  ],
  [RESOURCES.SEMESTER]: [
    USER_ACTIONS.CREATE,
    USER_ACTIONS.READ,
    USER_ACTIONS.UPDATE,
    USER_ACTIONS.DELETE,
  ],
  [RESOURCES.MAKEUP]: [
    USER_ACTIONS.CREATE,
    USER_ACTIONS.READ,
    USER_ACTIONS.UPDATE,
    USER_ACTIONS.DELETE,
  ],
  [RESOURCES.TRYOUT]: [
    USER_ACTIONS.CREATE,
    USER_ACTIONS.READ,
    USER_ACTIONS.UPDATE,
    USER_ACTIONS.DELETE,
  ],
  [RESOURCES.TAG]: [
    USER_ACTIONS.CREATE,
    USER_ACTIONS.READ,
    USER_ACTIONS.UPDATE,
    USER_ACTIONS.DELETE,
  ],
  [RESOURCES.EVALUATION]: [
    USER_ACTIONS.CREATE,
    USER_ACTIONS.READ,
    USER_ACTIONS.UPDATE,
    USER_ACTIONS.DELETE,
    USER_ACTIONS.ASSIGN_TEACHER_TO_EVAL,
  ],
  [RESOURCES.USER]: [
    USER_ACTIONS.LOGIN,
    USER_ACTIONS.CREATE,
    USER_ACTIONS.READ,
    USER_ACTIONS.UPDATE,
    USER_ACTIONS.DELETE,
    USER_ACTIONS.CREATE_SUPER_USER,
    USER_ACTIONS.DELETE_SUPER_USER,
  ],
  [RESOURCES.FRONT_DESK_VIEW]: [USER_ACTIONS.READ],
  [RESOURCES.TEACHER_VIEW]: [USER_ACTIONS.READ],
} as const

// Generate permission checks organized by resource
const generatePermissionChecksByResource = (
  role: EnumValues<typeof USER_ROLES>
) => {
  const resourceChecks: Record<
    string,
    Array<{ title: string; isTrue: boolean; key: string }>
  > = {}

  Object.entries(RESOURCE_ACTIONS).forEach(([resource, actions]) => {
    const resourceKey = resource as keyof typeof RESOURCE_ACTIONS
    resourceChecks[resourceKey] = []

    actions.forEach((action) => {
      // Handle special case for LOGIN action
      if (action === USER_ACTIONS.LOGIN) {
        resourceChecks[resourceKey].push({
          title: "Can login",
          isTrue: hasPermission(role, RESOURCES.USER, USER_ACTIONS.LOGIN),
          key: `${resource}_${action}`,
        })
      } else {
        const title = `Can ${camelToWords(action).toLowerCase()} ${camelToWords(resource).toLowerCase()}`
        const isTrue = hasPermission(
          role,
          resource as Resource,
          action as UserAction
        )
        const key = `${resource}_${action}`

        resourceChecks[resourceKey].push({ title, isTrue, key })
      }
    })
  })

  return resourceChecks
}

export const RBACExample = () => {
  const [role, setRole] = useState(USER_ROLES.SUPER)
  const permissionChecksByResource = generatePermissionChecksByResource(role)

  // Calculate total permissions for display
  const totalPermissions = Object.values(permissionChecksByResource).reduce(
    (total, checks) => total + checks.length,
    0
  )

  return (
    <Card style={{ width: 1275, margin: "20px auto" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Flex gap={10}>
          RBAC System Example
          <Popover
            content={
              <Flex col gap={10} style={{ width: 700 }}>
                <Title level={5}>Role Inheritance Demonstration:</Title>
                <Text>
                  The system uses role inheritance where higher roles inherit
                  permissions from lower roles:
                  <br />
                  Disabled (0) → Assistant (1) → Teacher (2) → Lead Teacher (3)
                  → Front Desk (4) → Manager (5) → Super (6)
                  <br />
                  <br />
                  For example, a Manager can do everything an Assistant,
                  Teacher, Lead Teacher, and Front Desk can do, plus additional
                  manager-specific permissions like managing users and viewing
                  contact info.
                </Text>
              </Flex>
            }
          >
            <InfoCircleOutlined />
          </Popover>
        </Flex>
        <Title level={4}>
          <Flex gap={10}>
            Current User Role:{" "}
            <Select
              style={{ width: 150 }}
              options={Object.values(USER_ROLES).map((role) => ({
                label: camelToWords(role),
                value: role,
              }))}
              value={role}
              onChange={(value) => setRole(value)}
            />
          </Flex>
        </Title>

        <div>
          <Title level={5}>
            All Permission Checks ({totalPermissions} total):
          </Title>

          <Space wrap align="start">
            {Object.entries(permissionChecksByResource).map(
              ([resource, checks]) => (
                <Card
                  key={resource}
                  style={{
                    backgroundColor: "white",
                    height:
                      resource === RESOURCES.USER ||
                      resource === RESOURCES.EVALUATION
                        ? 225
                        : 170,
                    width: 300,
                    fontSize: 12,
                  }}
                >
                  <Flex col gap={12}>
                    <Text strong>
                      {camelToWords(resource)} (
                      {checks.filter((c) => c.isTrue).length}/{checks.length}{" "}
                      permissions)
                    </Text>
                    <ul style={{ listStyleType: "none", margin: 0 }}>
                      {checks.map(({ title, isTrue, key }) => (
                        <Example key={key} title={title} isTrue={isTrue} />
                      ))}
                    </ul>
                  </Flex>
                </Card>
              )
            )}
          </Space>
        </div>

        <Flex col gap={20}>
          <Title level={5}>Protected Components:</Title>

          <Flex>
            <LoginProtected
              fallback={
                <Text type="secondary">
                  You don't have permission to login.
                </Text>
              }
            >
              <Button type="primary">Login</Button>
            </LoginProtected>
          </Flex>

          <Flex>
            <ProtectedComponent
              resource={RESOURCES.USER}
              action={USER_ACTIONS.CREATE}
              fallback={
                <Text type="secondary">
                  You don't have permission to create users
                </Text>
              }
            >
              <Button type="primary">Create User</Button>
            </ProtectedComponent>
          </Flex>

          <Flex>
            <ProtectedComponent
              resource={RESOURCES.USER}
              action={USER_ACTIONS.CREATE_SUPER_USER}
              fallback={
                <Text type="secondary">
                  You don't have permission to create super users
                </Text>
              }
            >
              <Button type="primary" danger>
                Create Super User
              </Button>
            </ProtectedComponent>
          </Flex>

          <ContactInfoProtected
            fallback={
              <Text type="secondary">
                Contact information is hidden for your role
              </Text>
            }
          >
            <Card>
              <p>Phone: (555) 123-4567</p>
              <p>Address: 123 Main St, City, State 12345</p>
              <p>Email: student@example.com</p>
            </Card>
          </ContactInfoProtected>
        </Flex>
      </Space>
    </Card>
  )
}
